import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  HostBinding,
} from '@angular/core';
import { MainMapComponent } from 'app/shared/components/main-map/main-map.component';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { TreeItemLike } from 'app/shared/components/tree-list/tree-list.component';
import { ToolMapType } from 'app/shared/models/tool-map-type';
import { WidgetModalType } from 'app/shared/models/widget-modal-type';
import { AuthService } from 'app/shared/services/auth.service';
import { ArrayUtils } from 'app/shared/utils/array.utils';
import { WidgetType } from 'app/shared/models/widget-type';
import { AppConfigService } from 'app/shared/services/app-config.service';
import {
  getToolMapActionLabelDictionary,
  getToolMapLabelDictionary,
  getWidgetMapLabelDictionary,
  getWidgetModalLabelDictionary,
} from 'app/shared/constants/widget.constant';
import { TitleCasePipe } from '@angular/common';
import { ToolMapActionType } from 'app/shared/models/tool-action-type';
import {
  AppTrackingService,
  TrackingToolType,
} from 'app/shared/services/app-tracking.service';
import sortBy from 'lodash/sortBy';
import { RoleType } from 'app/shared/models/role-type';
import { ScreenService } from 'app/shared/services/screen.service';
import { SideMenuService } from 'app/shared/services/side-menu.service';
import { TranslationService } from 'app/shared/services/translation.service';

interface CustomTreeItemLike extends TreeItemLike<CustomTreeItemLike> {
  invisible?: boolean;
}

@Component({
  selector: 'app-home-page',
  templateUrl: 'home.page.html',
  styleUrls: ['home.page.scss'],
})
export class HomePageComponent implements OnInit, OnDestroy {
  @ViewChild(MainMapComponent, { static: true })
  mainMapComp: MainMapComponent;

  @ViewChild('menuItemWithSlideToggleTpl', { static: true })
  menuItemWithSlideToggleTpl: TemplateRef<any>;

  @HostBinding('class.mobile-mode')
  isMobileScreen: boolean = false;

  get mainMapService() {
    return this.mainMapComp.mainMapService;
  }

  get canShowLogo() {
    return !this.appConfig.siiaMode;
  }

  menuTextEnabled: boolean = false;

  menuItems: CustomTreeItemLike[];

  private lifecycle = new Subject<void>();

  constructor(
    private authService: AuthService,
    public sideMenuService: SideMenuService,
    public appConfig: AppConfigService,
    public appTrackingService: AppTrackingService,
    private screenService: ScreenService,
    private translationService: TranslationService
  ) {}

  ngOnInit(): void {
    const titlePipe = new TitleCasePipe();

    const thisRef = new WeakRef(this);

    const createItemRelativeWidgetModal = (
      type: WidgetModalType,
      cfg?: {
        icon?: string;
        invisible?: boolean;
        visible?: boolean | Observable<boolean>;
      }
    ): CustomTreeItemLike => {
      const text = getWidgetModalLabelDictionary(this.translationService).get(type);

      let visible: boolean;
      const _subscribeVisible = () => {
        let value = cfg?.visible;
        if (!(value instanceof Observable)) {
          if (typeof value === 'undefined') {
            value = true;
          }
          value = of(!!value);
        }

        value.pipe(takeUntil(this.lifecycle)).subscribe((aBool) => {
          visible = aBool;
        });
      };

      return {
        invisible: !!cfg?.invisible,
        text,
        icon: cfg?.icon,
        get visible() {
          if (typeof visible !== 'boolean') {
            _subscribeVisible();
          }

          return !!visible;
        },
        get selected() {
          return thisRef.deref().mainMapService.isWidgetModalActive(type);
        },
        onClick: (e) => {
          e.preventDefault();
          thisRef.deref().mainMapService.toggleWidgetModal(type);
        },
      };
    };

    const createItemRelativeWidget = (
      type: WidgetType,
      invisible = false
    ): CustomTreeItemLike => {
      const text = getWidgetMapLabelDictionary(this.translationService).get(type);
      return {
        itemTemplate: this.menuItemWithSlideToggleTpl,
        invisible,
        text,
        get selected() {
          return thisRef.deref().mainMapService.isWidgetActive(type);
        },
        onClick: (e) => {
          e.preventDefault();
          thisRef.deref().mainMapService.toggleWidget(type);
        },
      };
    };

    const createItemRelativeTool = (
      type: ToolMapType,
      cfg?: {
        icon?: string;
        invisible?: boolean;
      }
    ): CustomTreeItemLike => {
      const text = getToolMapLabelDictionary(this.translationService).get(type);
      return {
        invisible: cfg?.invisible,
        icon: cfg?.icon,
        text,
        get selected() {
          return thisRef.deref().mainMapService.isToolMapActive(type);
        },
        onClick: (e) => {
          e.preventDefault();
          thisRef.deref().mainMapService.toggleToolMap(type);
        },
      };
    };

    const createItemRelativeToolAction = (
      type: ToolMapActionType,
      cfg?: {
        icon?: string;
        invisible?: boolean;
      }
    ): CustomTreeItemLike => {
      const text = getToolMapActionLabelDictionary(this.translationService).get(type);
      return {
        invisible: !!cfg?.invisible,
        icon: cfg?.icon,
        text,
        get disabled() {
          return !thisRef.deref().mainMapService.canRequestToolMapAction(type);
        },
        onClick: (e) => {
          e.preventDefault();
          thisRef.deref().mainMapService.requestToolMapAction(type);
        },
      };
    };

    const { externalMode, internalMode, siiaMode } = this.appConfig;

    const createIndicatorsMenu = (): CustomTreeItemLike => {
      const multipleLinksMode = this.appConfig.indicatorsLinks?.length > 1;

      const singleLink = this.appConfig.indicatorsLinks?.[0]?.link;

      return {
        icon: 'assets/icons/chart-simple.svg',
        text: this.translationService?.getCurrentLanguage()?.indicators || 'Indicadores',
        invisible: siiaMode,
        link: multipleLinksMode ? null : singleLink,
        linkTarget: '_blank',
        disabled: !(multipleLinksMode || singleLink),
        items: !multipleLinksMode
          ? null
          : this.appConfig.indicatorsLinks.map((x) => {
              return {
                text: this.translationService?.getCurrentLanguage()?.indicators || x.label,
                link: x.link,
                linkTarget: '_blank',
                disabled: !x.link,
              };
            }),
        onClick: () => {
          this.appTrackingService.send({
            type: TrackingToolType.Widget,
            name: this.translationService?.getCurrentLanguage()?.indicators || 'Indicadores',
            action: true,
          });
        },
      };
    };

    this.menuItems = [
      {
        icon: 'assets/icons/search.svg',
        text: this.translationService?.getCurrentLanguage()?.search || 'Pesquisar',
        items: [
          createItemRelativeWidgetModal(WidgetModalType.AdvancedSearch),
          createItemRelativeWidgetModal(WidgetModalType.SearchByDrawing),
          createItemRelativeTool(ToolMapType.Identify, {
            invisible: !siiaMode,
          }),
        ],
      },
      createItemRelativeWidgetModal(WidgetModalType.MapManagement, {
        icon: 'assets/icons/bookmark.svg',
        invisible: externalMode,
      }),
      createItemRelativeWidgetModal(WidgetModalType.LoadData, {
        icon: 'assets/icons/cloud-arrow-up.svg',
        invisible: !siiaMode,
        visible: this.authService.observeHasAnyRoleTypes(RoleType.Provider),
      }),
      createItemRelativeWidgetModal(WidgetModalType.AddLayer, {
        icon: 'assets/icons/layer-plus.svg',
      }),
      createItemRelativeTool(ToolMapType.ShareLink, {
        icon: 'assets/icons/share-nodes.svg',
        invisible: !externalMode,
      }),
      {
  icon: 'assets/icons/screwdriver-wrench.svg',
  text: this.translationService?.getCurrentLanguage()?.tools || 'Ferramentas',
        items: sortBy(
          [
            createItemRelativeTool(ToolMapType.AvailableCoverage, {
              invisible: siiaMode,
            }),
            createItemRelativeWidgetModal(WidgetModalType.ConvertCoordinate),
            createItemRelativeWidgetModal(WidgetModalType.Occurrence, {
              invisible: !siiaMode,
            }),
            createItemRelativeWidgetModal(WidgetModalType.RegisterEvent, {
              invisible: !siiaMode,
            }),
            createItemRelativeWidgetModal(WidgetModalType.Announcements, {
              invisible: !siiaMode,
            }),
            createItemRelativeToolAction(ToolMapActionType.Screenshot),
            createItemRelativeWidgetModal(WidgetModalType.Measurement),
            createItemRelativeToolAction(ToolMapActionType.Clear),
            createItemRelativeTool(ToolMapType.StreetView),
            createItemRelativeTool(ToolMapType.Identify, {
              invisible: siiaMode,
            }),
          ],
          (x) => x.text
        ),
      },
      {
  icon: 'assets/icons/globe.svg',
  text: this.translationService?.getCurrentLanguage()?.geoprocessing || 'Geoprocessamento',
        items: [
          createItemRelativeWidgetModal(WidgetModalType.HeatMap),
          createItemRelativeWidgetModal(WidgetModalType.ClusterMap),
          createItemRelativeWidgetModal(WidgetModalType.CoverageQuota, {
            invisible: !siiaMode,
          }),
          createItemRelativeWidgetModal(WidgetModalType.PropertyQuota, {
            invisible: !siiaMode,
          }),
          createItemRelativeWidgetModal(WidgetModalType.Path, {
            invisible: !siiaMode,
          }),
        ],
      },
      createItemRelativeWidgetModal(WidgetModalType.RegisterElement, {
        icon: 'assets/icons/objeto-cadastro.svg',
        invisible: !siiaMode,
        visible: this.authService.observeHasNotOnlyAnyOfThisRoleTypes(
          RoleType.Consumer
        ),
      }),
      {
  icon: 'assets/icons/pen.svg',
  text: this.translationService?.getCurrentLanguage()?.edit || 'Edição',
        items: [createItemRelativeWidgetModal(WidgetModalType.Notes)],
      },
      createItemRelativeWidgetModal(WidgetModalType.PrintMap, {
        icon: 'assets/icons/print.svg',
      }),
      {
  icon: 'assets/icons/compass.svg',
  text: this.translationService?.getCurrentLanguage()?.navigation || 'Navegação',
        items: [
          createItemRelativeToolAction(ToolMapActionType.InitialViewpoint),
          createItemRelativeToolAction(ToolMapActionType.PreviousViewpoint),
          createItemRelativeToolAction(ToolMapActionType.NextViewpoint),
          createItemRelativeTool(ToolMapType.Rotation),
          createItemRelativeTool(ToolMapType.CurrentLocation),
          createItemRelativeTool(ToolMapType.Fullscreen),
          createItemRelativeTool(ToolMapType.ZoomByWindow),
          createItemRelativeToolAction(ToolMapActionType.ZoomIn),
          createItemRelativeToolAction(ToolMapActionType.ZoomOut),
        ],
      },
      createIndicatorsMenu(),
      {
        icon: 'assets/icons/gear.svg',
        text: 'Configurações',
        invisible: !internalMode,
        items: [
          createItemRelativeWidget(WidgetType.Scales),
          createItemRelativeWidget(WidgetType.Coords),
          createItemRelativeWidget(WidgetType.Overview),
        ],
      },
      createItemRelativeWidgetModal(WidgetModalType.Help, {
        icon: 'assets/icons/help-outline.svg',
        invisible: siiaMode,
      }),
    ];

    this.removeInvisibleItems(this.menuItems);

    this.menuItems
      .filter((x) => x.items?.length)
      .forEach((item) => {
        Object.defineProperty(item, 'selected', {
          get: () => {
            return !!item.items?.some((x) => x.selected);
          },
        });
      });

    if (this.appConfig.internalMode) {
      this.menuItems.push({
        icon: 'assets/icons/power-off.svg',
        text: 'Sair',
        onClick: (e) => {
          e.preventDefault();
          this.authService.logOut();
        },
      });
    }

    this.menuItems.forEach((item) => {
      item.text = titlePipe.transform(item.text);
    });

    this.screenService
      .observeIsMobile()
      .pipe(takeUntil(this.lifecycle))
      .subscribe((isMobile) => {
        this.isMobileScreen = isMobile;
      });
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();
  }

  onSidemenuOpening() {
    this.menuTextEnabled = true;
  }

  onSidemenuClosed() {
    this.menuTextEnabled = false;
  }

  onSidemenuLogoClick(e) {
    e.stopPropagation();
    this.mainMapService.initialViewpoint();
  }

  onMapClick(e) {
    if (!this.isMobileScreen) {
      this.sideMenuService.close();
    }
  }

  private removeInvisibleItems(items: CustomTreeItemLike[]) {
    ArrayUtils.removeItemsBy(items, (x) => x.invisible);
    items.forEach((x) => {
      if (x.items?.length) {
        this.removeInvisibleItems(x.items);
      }
    });
  }
}
